/**
 * Thin frontend auth helpers — manages the app JWT in localStorage and the
 * login roundtrip with Google Identity Services.
 */

const TOKEN_KEY = 'shakti.appToken';
const USER_KEY = 'shakti.user';

export type UserRole = 'admin' | 'rm';

export interface AuthUser {
  email: string;
  name?: string;
  role: UserRole;
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AuthUser>;
    if (!parsed.email) return null;
    return {
      email: parsed.email,
      name: parsed.name,
      role: parsed.role === 'admin' ? 'admin' : 'rm',
    };
  } catch {
    return null;
  }
}

export function saveSession(token: string, user: AuthUser): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function isJwtExpired(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const claims = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))) as {
      exp?: number;
    };
    if (!claims.exp) return true;
    return claims.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

export async function authedFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(input, { ...init, headers });
  if (res.status === 401) {
    clearSession();
    window.location.reload();
  }
  return res;
}
